import { DownOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Accordion from '@/components/Accordion/Accordion';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import { ContentTitle, ModalTitle } from '@/components/Typography/NamedTypo';
import { SpanStyle } from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import { customScrollbar, modalStyle } from '@/styles/style';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { getTimezoneDayjs } from '@/utils/time';

import { buildTaxonomyFeedbackPath } from '../../utils/routes';
import EntireDateSelector from './EntireDateSelector';

type Props<T> = T extends 'CREATE' ? DefaultProps<T> : EditModeProps<T>;

type DefaultProps<T> = {
  mode: T;
};

type EditModeProps<T> = DefaultProps<T> & {
  taxonomyAlias: string;
};

const modeLabelMap: Record<EDIT_MODE, string> = {
  CREATE: 'Create',
  EDIT: 'Edit',
};

const TaxonomySetupModal = <T extends EDIT_MODE>(props: Props<T>) => {
  const wr = useWorkspaceRouter();
  const { parseStructuredFilter } = useStructuredFilter();
  const queryClient = useQueryClient();
  const addToast = useToast();
  const modeLabel = modeLabelMap[props.mode];
  const { closeModal } = useModalContext();

  const { state, setValue, setState } = useStates({
    title: '',
    desc: '',
  });

  const [filterAccordionOpen, setFilterAccordionOpen] = useState({
    init: false,
    status: false,
  });

  const [filterChanged, setFilterChanged] = useState(false);
  const {
    filterQueryFormats: currFilterSetQuery,
    onFilterSetChange,
    clearFilter,
  } = useStructuredFilterQuery({
    onFilterChangeCallback: () => setFilterChanged(true),
    clearFilterCallback: () => setFilterChanged(true),
  });

  const [date, setDate] = useState<Dayjs | null>(
    getTimezoneDayjs().addVal(-3, 'month'),
  );

  const { data } = useQuery({
    queryKey: [
      queryKeys.taxonomy.TAXONOMY_SETUP,
      props.mode === 'EDIT' && props.taxonomyAlias,
    ],
    queryFn: () => {
      if (props.mode !== 'EDIT') throw new Error('INVALID MODE');
      return service.taxonomy.fetchTaxonomyItem(props.taxonomyAlias);
    },
    enabled: props.mode === 'EDIT',
  });

  useEffect(() => {
    if (data) {
      setState({
        title: data.title,
        desc: data.desc,
      });
      const filter = new SFilter(data.filter || {});
      const startDate = filter.getDateFilter().start_date;
      const dataFilter = filter.getDataFilterMap();
      setDate(startDate ? getTimezoneDayjs(startDate) : null);
      const filterFormats = parseStructuredFilter(dataFilter);
      onFilterSetChange(filterFormats);

      if (data.filter || startDate) {
        setFilterAccordionOpen({
          init: true,
          status: true,
        });
      } else {
        setFilterAccordionOpen({
          init: true,
          status: false,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const currParams: TaxonomySetupT = useMemo(() => {
    let params: TaxonomySetupT = {
      title: state.title,
      desc: state.desc,
    };
    const structuredFilter =
      structuredFilterUtils.buildStructuredFilter(currFilterSetQuery);
    const filter = new SFilter(structuredFilter);

    filter.setDateFilter({
      start_date: date?.toJSON() || null,
      end_date: null,
    });

    params.filter = filter.get();

    return params;
  }, [JSON.stringify(currFilterSetQuery), date, state.desc, state.title]);

  const onSubmit = async () => {
    switch (props.mode) {
      case 'CREATE': {
        const res = await service.taxonomy.createTaxonomyItem(currParams);
        addToast('Taxonomy created', 'success');
        queryClient.invalidateQueries({ queryKey: queryKeys.taxonomy.TAXONOMY_LIST });
        wr.push(buildTaxonomyFeedbackPath(res.alias));
        break;
      }
      case 'EDIT': {
        await service.taxonomy.editTaxonomyItem(
          props.taxonomyAlias,
          currParams,
        );
        addToast('Taxonomy edited', 'success');
        queryClient.invalidateQueries({ queryKey: queryKeys.taxonomy.TAXONOMY_LIST });
        queryClient.invalidateQueries({ queryKey: [
          ...queryKeys.taxonomy.TAXONOMY_ITEM,
          props.taxonomyAlias,
        ] });
        break;
      }
      default:
        throw new Error('Invalid mode');
    }
    closeModal();
  };

  const CTACondition = useMemo(() => {
    if (props.mode === 'CREATE') {
      if (state.title.trim() === '') return false;
      return true;
    }
    const since = data?.filter?.[FEEDBACK_COLUMNS.DATE_START] as
      | string
      | undefined;

    if (!data) return false;
    if (data.title !== state.title) return true;
    if (data.desc !== state.desc) return true;
    if (getTimezoneDayjs(since).toJSON() !== date?.toJSON()) return true;
    if (filterChanged) return true;
    return false;
  }, [data, date, filterChanged, props.mode, state.desc, state.title]);

  const filterInitialOpen = useMemo(() => {
    if (props.mode === 'CREATE')
      return {
        init: true,
        status: true,
      };
    return filterAccordionOpen;
  }, [filterAccordionOpen, props.mode]);

  const onDateChange = (date: Dayjs | null) => {
    setDate(date);
  };

  return (
    <Style g={40}>
      <VFlex g={32}>
        <ModalTitle>{modeLabel} taxonomy</ModalTitle>
        <VFlex g={32}>
          <VFlex g={8}>
            <ContentTitle>Taxonomy name</ContentTitle>
            <Input
              sizeType="m"
              placeholder="type name"
              useBlur={false}
              value={state.title}
              onChange={(e) => setValue('title', e.target.value)}
            />
          </VFlex>
          <VFlex g={8}>
            <ContentTitle>Description</ContentTitle>
            <FlexibleTextarea
              style={{ width: '100%', maxHeight: 400 }}
              showBorder
              placeholder="type description of this taxonomy"
              minHeight={75}
              value={state.desc}
              onChange={(e) => setValue('desc', e.target.value)}
            />
          </VFlex>
        </VFlex>
        <VFlex g={16}>
          {filterInitialOpen.init && (
            <Accordion
              initialOpen={filterInitialOpen.status}
              anchor={(isOpened) => (
                <AnchorWrapper a="center" g={6}>
                  <DownOutlined
                    style={{
                      rotate: isOpened ? '-180deg' : '0deg',
                      transition: 'all 0.2s',
                    }}
                  />
                  <ContentTitle>Feedback filter</ContentTitle>
                </AnchorWrapper>
              )}
              content={
                <VFlex g={12}>
                  <Flex g={12}>
                    <FilterLabelWrapper>Date from</FilterLabelWrapper>
                    <EntireDateSelector date={date} onChange={onDateChange} />
                  </Flex>
                  <Flex g={12} a="flex-start">
                    <FilterLabelWrapper>Filter</FilterLabelWrapper>
                    <Flex f={1}>
                      <StructuredFilter
                        clearFilter={clearFilter}
                        disAllowFilter={[FEEDBACK_COLUMNS.TAXONOMY_TOPIC]}
                        values={currFilterSetQuery}
                        onChange={onFilterSetChange}
                      />
                    </Flex>
                  </Flex>
                </VFlex>
              }
            />
          )}
        </VFlex>
      </VFlex>
      <Flex j="flex-end" g={12}>
        <Button variant="outlined" sizeType="m" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          sizeType="m"
          type="submit"
          onClick={onSubmit}
          disabled={!CTACondition}
        >
          {modeLabel}
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 600px;
  height: fit-content;
  padding: 32px;
  max-height: 90vh;
  overflow: auto;
  ${modalStyle};
  ${customScrollbar};
`;

const AnchorWrapper = styled(Flex)`
  width: fit-content;
`;

const FilterLabelWrapper = styled(Flex)`
  ${SpanStyle({
    type: 'label2',
    weight: 'r',
  })};
  color: ${(props) => props.theme.colors.gray600};
  width: 120px;
  height: 32px;
  align-items: center;
`;

export default TaxonomySetupModal;
